import { useMemo } from 'react';

const HOUR_PX = 52;   // pixels per hour — controls the vertical scale
const TOTAL_H = 24 * HOUR_PX;

function minutesToPx(min) {
  return (min / 60) * HOUR_PX;
}

function timeToMin(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m || 0);
}

function isoToMin(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

function minToLabel(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const SLEEP_LANE_WIDTH = '46%';
const EVENT_LANE_LEFT  = '50%';

/**
 * DayTimeline — CSS-positioned 24-hour view.
 *
 * Props:
 *   sleepItems   {Array}  sleepTracking docs for the day (startTime/endTime HH:MM, type)
 *   feedItems    {Array}  bottleFeeds docs for the day
 *   breastItems  {Array}  breastfeeding docs for the day
 *   diaperItems  {Array}  diapers docs for the day
 *   waterItems   {Array}  waterIntake docs for the day
 *   solidItems   {Array}  solidFoods docs for the day
 */
export default function DayTimeline({
  sleepItems  = [],
  feedItems   = [],
  breastItems = [],
  diaperItems = [],
  waterItems  = [],
  solidItems  = [],
}) {
  // Build the sleep bars
  const sleepBars = useMemo(() => {
    return sleepItems.map(item => {
      const startMin = timeToMin(item.startTime);
      let endMin     = timeToMin(item.endTime);
      if (startMin === null) return null;
      if (endMin === null) endMin = Math.min(startMin + 60, 23 * 60 + 59);
      // Overnight: clamp to end of day for this segment
      if (endMin <= startMin) endMin = 23 * 60 + 59;
      return {
        startMin,
        endMin,
        type:  item.type,
        label: item.type === 'nap' ? 'Nap' : 'Night sleep',
      };
    }).filter(Boolean);
  }, [sleepItems]);

  // Build event dots: { min, icon, color, detail }
  const events = useMemo(() => {
    const all = [];

    feedItems.forEach(i => {
      const min = isoToMin(i.date);
      if (min !== null) all.push({ min, icon: '🍼', color: '#2196f3', detail: i.amount ? `${i.amount} oz` : '' });
    });
    breastItems.forEach(i => {
      const min = isoToMin(i.date);
      if (min !== null) all.push({ min, icon: '🤱', color: '#e91e63', detail: i.duration ? `${i.duration}m` : '' });
    });
    diaperItems.forEach(i => {
      const min = isoToMin(i.date);
      if (min !== null) all.push({ min, icon: '🧷', color: '#ff9800', detail: i.type || '' });
    });
    waterItems.forEach(i => {
      const min = isoToMin(i.date);
      if (min !== null) all.push({ min, icon: '💧', color: '#00bcd4', detail: i.amount ? `${i.amount} ml` : '' });
    });
    solidItems.forEach(i => {
      const min = isoToMin(i.date);
      if (min !== null) all.push({ min, icon: '🥘', color: '#4caf50', detail: i.foodName || '' });
    });

    return all.sort((a, b) => a.min - b.min);
  }, [feedItems, breastItems, diaperItems, waterItems, solidItems]);

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div
      style={{
        display: 'flex',
        gap: 0,
        position: 'relative',
        userSelect: 'none',
      }}
    >
      {/* ── Time label column ── */}
      <div style={{ width: 44, flexShrink: 0, position: 'relative', height: TOTAL_H }}>
        {hours.map(h => (
          <div
            key={h}
            style={{
              position: 'absolute',
              top:      h === 0 ? 2 : h * HOUR_PX - 8,
              right:    6,
              fontSize: '0.68rem',
              color:    'var(--text-light)',
              lineHeight: 1,
            }}
          >
            {String(h).padStart(2, '0')}:00
          </div>
        ))}
      </div>

      {/* ── Main grid ── */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          height: TOTAL_H,
          borderLeft: '2px solid var(--border-light)',
        }}
      >
        {/* Hour grid lines */}
        {hours.map(h => (
          <div
            key={h}
            style={{
              position: 'absolute',
              top:      h * HOUR_PX,
              left:     0,
              right:    0,
              height:   1,
              background: h % 6 === 0
                ? 'var(--border-light)'
                : 'rgba(0,0,0,0.04)',
              zIndex: 0,
            }}
          />
        ))}

        {/* ── Sleep bars (left lane) ── */}
        {sleepBars.map((bar, i) => {
          const isNap   = bar.type === 'nap';
          const top     = minutesToPx(bar.startMin);
          const height  = Math.max(6, minutesToPx(bar.endMin - bar.startMin));
          const bg      = isNap ? 'rgba(76,175,80,0.2)' : 'rgba(63,81,181,0.18)';
          const border  = isNap ? '#4caf50' : '#3f51b5';
          const textCol = isNap ? '#2e7d32' : '#283593';

          return (
            <div
              key={i}
              title={`${bar.label}: ${minToLabel(bar.startMin)} – ${minToLabel(bar.endMin)}`}
              style={{
                position:   'absolute',
                top,
                left:       4,
                width:      SLEEP_LANE_WIDTH,
                height,
                borderRadius: 4,
                background: bg,
                borderLeft: `3px solid ${border}`,
                display:    'flex',
                alignItems: 'center',
                paddingLeft: 6,
                fontSize:   '0.65rem',
                color:      textCol,
                fontWeight: 600,
                overflow:   'hidden',
                whiteSpace: 'nowrap',
                cursor:     'default',
                zIndex:     1,
              }}
            >
              {height > 20 ? bar.label : ''}
            </div>
          );
        })}

        {/* ── Event dots (right lane) ── */}
        {events.map((ev, i) => (
          <div
            key={i}
            title={`${ev.icon} ${minToLabel(ev.min)}${ev.detail ? ` · ${ev.detail}` : ''}`}
            style={{
              position:   'absolute',
              top:        minutesToPx(ev.min) - 10,
              left:       EVENT_LANE_LEFT,
              display:    'flex',
              alignItems: 'center',
              gap:        4,
              zIndex:     2,
              cursor:     'default',
            }}
          >
            <span
              style={{
                display:         'inline-flex',
                alignItems:      'center',
                justifyContent:  'center',
                width:           20,
                height:          20,
                borderRadius:    '50%',
                background:      ev.color + '22',
                border:          `1.5px solid ${ev.color}`,
                fontSize:        '0.65rem',
                flexShrink:      0,
              }}
            >
              {ev.icon}
            </span>
            <span
              style={{
                fontSize:   '0.65rem',
                color:      'var(--text-light)',
                whiteSpace: 'nowrap',
              }}
            >
              {minToLabel(ev.min)}{ev.detail ? ` ${ev.detail}` : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
